import React from 'react';
import { Form } from '@dashboard/components/Formik';
import parseBoolean from '@dashboard/services/parseBoolean';
import {
  useAddFunnelStep,
  useUpdateFunnelStep,
} from '@dashboard/hooks/queries/usePageFunnelQueries';
import * as Yup from 'yup';
import usePage from '@dashboard/hooks/usePage';

export default function FunnelStepsForm() {
  const { page } = usePage();
  const updateFunnelStep = useUpdateFunnelStep(page._id);
  const addFunnelStep = useAddFunnelStep(page._id);

  const upsellFunnelStep = page.funnelSteps && page.funnelSteps[0];

  const defaultUpsellFunnelStep = {
    enabled: 'false',
    type: 'upsell',
    config: {
      action: 'checkout',
      checkout: undefined,
      redirect: {
        url: '',
      },
    },
  };

  return (
    <Form
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={Yup.object().shape({
        enabled: Yup.boolean().required(),
        type: Yup.string().required(),
        config: Yup.object().when('enabled', {
          is: true,
          then: Yup.object().shape({
            action: Yup.string().required('Upsell step is required'),
            checkout: Yup.string().when('action', {
              is: 'checkout',
              then: Yup.string().required('Checkout is required'),
            }),
            redirect: Yup.object().when('action', {
              is: 'redirect',
              then: Yup.object().shape({
                url: Yup.string().required('Redirect URL is required'),
              }),
            }),
          }),
        }),
      })}
      initialValues={
        upsellFunnelStep
          ? {
              enabled: upsellFunnelStep.enabled.toString(),
              type: upsellFunnelStep.type,
              config: {
                action: upsellFunnelStep.config.action,
                checkout: upsellFunnelStep.config.checkout,
                redirect: {
                  url: upsellFunnelStep.config.redirect?.url || '',
                },
              },
            }
          : defaultUpsellFunnelStep
      }
      fields={({ values }) => [
        {
          name: 'enabled',
          label: 'One-click upsell',
          type: 'radioCard',
          options: [
            {
              value: 'false',
              label: 'No upsell',
            },
            {
              value: 'true',
              label: 'Show one-click upsell',
            },
          ],
        },
        {
          name: 'config.action',
          label: 'After payment step',
          tooltip:
            'Redirect customers to a new page with an upsell or swap the checkout page for an upsell after successful payment',
          type: 'radioCard',
          options: [
            {
              value: 'checkout',
              label: 'Show upsell on same page',
            },
            {
              value: 'redirect',
              label: 'Redirect to page with upsell',
            },
          ],
          hidden: parseBoolean(values.enabled) === false,
          required: parseBoolean(values.enabled),
        },
        {
          name: 'config.checkout',
          label: 'Upsell checkout page',
          tooltip:
            'Display one of your checkout pages as an upsell, in the same way as your current checkout is displayed (embed, pop-up, payment link)',
          type: 'pageSelect',
          hidden: parseBoolean(values.enabled) === false || values.config.action !== 'checkout',
          pageTypes: ['checkout', 'event'],
          required: values.config.action === 'checkout',
          currentCheckoutId: page._id,
        },
        {
          name: 'config.redirect.url',
          label: 'Page URL',
          tooltip:
            'Any checkout pages embedded on this URL will be displayed as an upsell. Note: the initial purchase must be made on the same domain as the upsell.',
          placeholder: 'https://',
          type: 'url',
          hidden: parseBoolean(values.enabled) === false || values.config.action !== 'redirect',
        },
        /* {
          name: 'upsell.redirect.path',
          label: 'Redirect path',
          type: 'text',
          hidden: values.upsell?.action !== 'redirect',
        },
        {
          name: 'upsell.redirect.query',
          label: 'Redirect query',
          type: 'text',
          hidden: values.upsell?.action !== 'redirect',
        }, */
      ]}
      onSubmit={async values => {
        try {
          if (upsellFunnelStep?._id) {
            const response = await updateFunnelStep.mutateAsync({
              funnelStepId: upsellFunnelStep._id,
              updates: {
                type: 'upsell',
                enabled: parseBoolean(values.enabled),
                config: {
                  action: values.config.action,
                  checkout: values.config.checkout,
                  redirect: values.config.redirect,
                },
              },
            });

            const funnelStep = response.funnelSteps.find(
              funnelStep => funnelStep._id === upsellFunnelStep._id,
            );

            return {
              data: {
                enabled: funnelStep.enabled.toString(),
                type: funnelStep.type,
                config: {
                  action: funnelStep.config.action,
                  checkout: funnelStep.config.checkout,
                  redirect: {
                    url: funnelStep.config.redirect?.url || '',
                  },
                },
              },
            };
          } else {
            const response = await addFunnelStep.mutateAsync({
              updates: {
                type: 'upsell',
                enabled: parseBoolean(values.enabled),
                config: {
                  action: values.config.action,
                  checkout: values.config.checkout,
                  redirect: values.config.redirect,
                },
              },
            });

            // get last funnel step
            const funnelStep = response.funnelSteps[response.funnelSteps.length - 1];

            return {
              data: {
                enabled: funnelStep.enabled.toString(),
                type: funnelStep.type,
                config: {
                  action: funnelStep.config.action,
                  checkout: funnelStep.config.checkout,
                  redirect: {
                    url: funnelStep.config.redirect?.url || '',
                  },
                },
              },
            };
          }
        } catch (err) {
          return {
            error: err.message,
          };
        }
      }}
      submitButton={{
        label: 'Save',
      }}
      showState={true}
    />
  );
}
